<!--
  Everything about a campaign that isn't needed to actually create it --
  moved out of CreateCampaignDialog.vue's "circus" of fields into its own
  home on the campaign's Settings tab (CampaignDetailPage.vue), so create/edit
  stays to the handful of fields that define what the campaign IS (name,
  products, type, schedule, who works it, distribution), and everything
  tunable afterward -- lead filters, the call script, transfer routing,
  auto-dial, adoption behavior -- lives here instead, saved independently.
-->
<template>
  <div class="tw-adv-settings">
    <section class="tw-section">
      <div class="tw-section__head">
        <span class="tw-section__title">Campaign details</span>
      </div>
      <div class="tw-section__body tw-form-grid">
        <label class="tw-form-grid__full">
          <span class="tw-field-label">Campaign name<span class="tw-required">*</span></span>
          <input
            v-model="form.task_name"
            type="text"
            class="tw-input"
            placeholder="e.g. July outbound follow-ups"
            :disabled="disabled"
          />
        </label>

        <label class="tw-form-grid__full">
          <span class="tw-field-label">
            Description <span style="font-weight: 400; opacity: 0.65">optional</span>
          </span>
          <textarea
            v-model="form.description"
            class="tw-textarea"
            rows="2"
            placeholder="What is this campaign about?"
            :disabled="disabled"
          />
        </label>

        <label>
          <span class="tw-field-label">Product<span class="tw-required">*</span></span>
          <select v-model="selectedContext" class="tw-select" :disabled="disabled">
            <option value="" disabled>Select product</option>
            <option v-for="opt in contextOptions" :key="opt.value" :value="opt.value">{{ opt.title }}</option>
          </select>
          <p class="tw-field-hint">
            Changing this re-selects leads in the background -- existing leads are always kept.
          </p>
        </label>

        <label>
          <span class="tw-field-label">Campaign type</span>
          <div class="tw-badge tw-badge--info" style="width: fit-content">
            {{ isRecurringReminder ? 'Recurring reminder' : 'One-time campaign' }}
          </div>
          <p class="tw-field-hint">Set at creation -- can't be changed afterward.</p>
        </label>

        <template v-if="!isRecurringReminder">
          <label>
            <span class="tw-field-label">Start date<span class="tw-required">*</span></span>
            <input v-model="form.start_date" type="date" class="tw-select" :disabled="disabled" />
          </label>
          <label>
            <span class="tw-field-label">End date<span class="tw-required">*</span></span>
            <input v-model="form.end_date" type="date" class="tw-select" :disabled="disabled" />
          </label>
        </template>

        <template v-else>
          <label class="tw-form-grid__full">
            <span class="tw-field-label">Schedule type<span class="tw-required">*</span></span>
            <select v-model="form.schedule_type" class="tw-select" :disabled="disabled">
              <option v-for="opt in SCHEDULE_TYPE_OPTIONS" :key="opt.value" :value="opt.value">{{ opt.title }}</option>
            </select>
            <p class="tw-field-hint">{{ scheduleTypeHint }}</p>
          </label>

          <div class="tw-form-grid__full">
            <FieldWithLeadOverride
              v-if="!isOneTimeSchedule"
              label="Every"
              :field-hint="scheduleFrequencyUnitLabel"
              v-model:value="form.schedule_frequency"
              v-model:override-field="form.schedule_frequency_field"
              value-type="number"
              :value-min="1"
              value-placeholder="e.g. 3"
              :override-options="numberFilterFieldOptions"
              override-placeholder="e.g. Contract Reminder Interval"
              :disabled="disabled"
              :can-use-filters="canUseScheduleFilters"
            />
          </div>

          <div class="tw-form-grid__full">
            <FieldWithLeadOverride
              v-if="form.schedule_type === 'WEEKLY'"
              label="Day of the week"
              v-model:value="form.schedule_day_of_week"
              v-model:override-field="form.schedule_day_of_week_field"
              value-type="select"
              :value-items="WEEKDAY_OPTIONS"
              :override-options="filterFieldOptions"
              override-placeholder="e.g. Preferred Day"
              :disabled="disabled"
              :can-use-filters="canUseScheduleFilters"
            />
          </div>

          <div class="tw-form-grid__full">
            <FieldWithLeadOverride
              v-if="form.schedule_type === 'MONTHLY'"
              label="Day of the month"
              v-model:value="form.schedule_day_of_month"
              v-model:override-field="form.schedule_day_of_month_field"
              value-type="select"
              :value-items="DAY_OF_MONTH_OPTIONS"
              :override-options="filterFieldOptions"
              override-placeholder="e.g. Billing Day"
              :disabled="disabled"
              :can-use-filters="canUseScheduleFilters"
            />
          </div>

          <label v-if="isOneTimeSchedule" class="tw-form-grid__full">
            <span class="tw-field-label">Date and time to call</span>
            <p class="tw-field-hint">
              Each lead is called once, at the exact date (and, unless Call time below is set, the
              exact time too) resolved below.
            </p>
          </label>

          <div class="tw-form-grid__full">
            <FieldWithLeadOverride
              v-if="isOneTimeSchedule"
              label="Date to call"
              field-hint="pick one date for every lead, or read each lead's own date field"
              v-model:value="form.anchor_date"
              v-model:override-field="form.anchor_field"
              value-type="date"
              :override-options="dateFilterFieldOptions"
              override-placeholder="e.g. Preferred Call Date"
              :disabled="disabled"
              :can-use-filters="canUseScheduleFilters"
            />
          </div>
          <p v-if="isOneTimeSchedule && form.anchor_field" class="tw-field-hint tw-form-grid__full">
            Two ways to use a lead field: point it at a field that already holds the full date and
            time together and leave Call time below blank -- or point it at a plain calendar-date
            field and set Call time below for the hour.
          </p>

          <div class="tw-form-grid__full">
            <FieldWithLeadOverride
              label="Call time"
              :field-hint="isOneTimeSchedule ? '24-hour, e.g. 14:30 -- optional, see below' : '24-hour, e.g. 14:30'"
              v-model:value="form.schedule_time"
              v-model:override-field="form.schedule_time_field"
              value-type="time"
              :override-options="timeFilterFieldOptions"
              override-placeholder="e.g. Preferred Call Time"
              :disabled="disabled"
              :can-use-filters="canUseScheduleFilters"
              :required="!isOneTimeSchedule"
            />
          </div>
          <p v-if="isOneTimeSchedule" class="tw-field-hint tw-form-grid__full">
            Leave this blank if the date above already resolves to the exact date <em>and</em> time
            together. Set it only if that resolves to just a calendar date and the time comes from
            here instead.
          </p>

          <div v-if="!isOneTimeSchedule" class="tw-form-grid__full">
            <span class="tw-field-label">
              {{ form.schedule_type === 'YEARLY' ? 'Reference date' : 'Count from' }}
            </span>
            <select
              :value="anchorMode"
              class="tw-select"
              style="max-width: 320px"
              :disabled="disabled"
              @change="anchorMode = $event.target.value"
            >
              <option value="CAMPAIGN">
                {{ form.schedule_type === 'YEARLY' ? 'Same date for every lead' : 'Campaign start date' }}
              </option>
              <option value="LEAD_FIELD">A lead field</option>
            </select>
            <p class="tw-field-hint">
              {{
                form.schedule_type === 'YEARLY'
                  ? (anchorMode === 'CAMPAIGN'
                      ? 'Every lead is wished on the same calendar date below, repeating on that month/day every year.'
                      : "Each lead's own date in the field below sets the month/day this repeats on every cycle.")
                  : anchorMode === 'CAMPAIGN'
                    ? 'Every lead is reminded on the same shared clock, starting from when this campaign began.'
                    : 'Each lead counts from its own value in the field below -- staggers naturally.'
              }}
            </p>
          </div>

          <label v-if="form.schedule_type === 'YEARLY' && anchorMode === 'CAMPAIGN'">
            <span class="tw-field-label">Date<span class="tw-required">*</span></span>
            <input v-model="form.anchor_date" type="date" class="tw-select" :disabled="disabled" />
            <p class="tw-field-hint">Only the month and day are used -- the year just anchors the first cycle.</p>
          </label>

          <label v-if="!isOneTimeSchedule && anchorMode === 'LEAD_FIELD'">
            <span class="tw-field-label">Lead field<span class="tw-required">*</span></span>
            <select v-model="form.anchor_field" class="tw-select" :disabled="disabled || !canUseScheduleFilters">
              <option value="" disabled>Select field</option>
              <option v-for="opt in dateFilterFieldOptions" :key="opt.key" :value="opt.key">{{ opt.label }}</option>
            </select>
          </label>
        </template>
      </div>
    </section>

    <section class="tw-section">
      <div class="tw-section__head">
        <span class="tw-section__title">Calling setup</span>
      </div>
      <div class="tw-section__body tw-form-grid">
        <label>
          <span class="tw-field-label">Handled by<span class="tw-required">*</span></span>
          <select v-model="form.handled_by" class="tw-select" :disabled="disabled || isRecurringReminder">
            <option v-for="opt in handledByOptions" :key="opt.value" :value="opt.value">{{ opt.title }}</option>
          </select>
          <p v-if="isRecurringReminder" class="tw-field-hint">
            Recurring reminder campaigns always run through the AI agent.
          </p>
        </label>

        <label class="tw-form-grid__full">
          <span class="tw-field-label">
            {{ isAiCampaign ? 'AI agents' : 'Human agents' }}<span class="tw-required">*</span>
          </span>
          <v-select
            v-model="form.assigned_agent_ids"
            :items="agentOptions"
            item-title="title"
            item-value="value"
            :placeholder="agentOptions.length ? 'Select agents' : 'No matching agents found'"
            variant="outlined"
            density="comfortable"
            hide-details
            multiple
            chips
            closable-chips
            :disabled="disabled"
          />
        </label>

        <!-- The transfer router that actually places these calls is
        org-wide, not scoped to whichever campaign answered (see
        audiosocket_server.py's router comment): it builds its department
        list from EVERY task in the org with a transfer target configured,
        regardless of that task's own handled_by, and offers all of them on
        every inbound call. So this isn't just for AI campaigns or the
        org's one default-inbound campaign -- a caller who belongs to a
        purely Human-agent outbound campaign can still call the org's main
        number back, which is always answered by the AI first, and the AI
        can only route them to that campaign's own team if a transfer
        target was configured here. Offered on every campaign type
        unconditionally for that reason -- KAN-30 follow-up, 2026-08-19. -->
        <label class="tw-form-grid__full">
          <span class="tw-field-label">Transfer calls to</span>
          <v-select
            v-model="form.redirect_targets"
            :items="redirectTargetOptions"
            item-title="title"
            item-value="value"
            :placeholder="redirectTargetOptions.length ? 'Select who a call is handed over to' : 'No agents with a softphone or mobile number on file'"
            variant="outlined"
            density="comfortable"
            hide-details
            multiple
            chips
            closable-chips
            :disabled="disabled"
          />
          <p class="tw-field-hint">
            When a caller to the org's main number needs a human, or asks for one, these people
            are tried in order until one answers -- each on their softphone or their own mobile,
            within their own working hours. Not limited to this campaign's own agents -- anyone
            at the org can be a transfer target. Every inbound call is answered by the AI first,
            regardless of which campaign it's for, so this applies even on a campaign handled by
            human agents. Leave empty to never offer a transfer on this campaign.
          </p>
        </label>

        <label v-if="form.redirect_targets.length" class="tw-form-grid__full">
          <span class="tw-field-label">What this is for</span>
          <input
            v-model="form.redirect_description"
            type="text"
            class="tw-input"
            placeholder="e.g. Sales enquiries and new customer calls"
            maxlength="200"
            :disabled="disabled"
          />
          <p class="tw-field-hint">
            A caller-facing description of who this reaches. If more than one of your
            campaigns has a transfer target, the AI asks the caller who they want and matches
            their answer against this description -- not this campaign's own name -- so leaving
            it blank makes misrouting more likely whenever another campaign also has a transfer
            set up.
          </p>
        </label>

        <label v-if="form.redirect_targets.length" class="tw-form-grid__full">
          <span class="tw-field-label">Handover message</span>
          <input
            v-model="form.redirect_message"
            type="text"
            class="tw-input"
            placeholder="Please hold while I transfer your call to a member of our team."
            maxlength="300"
            :disabled="disabled"
          />
          <p class="tw-field-hint">
            What the AI says right before connecting the call. Leave blank to use the default line
            above.
          </p>
        </label>
      </div>
    </section>

    <section class="tw-section">
      <div class="tw-section__head">
        <span class="tw-section__title">Lead filters</span>
      </div>
      <div class="tw-section__body">
        <p class="tw-insight__muted tw-insight__muted--tight">
          Same field → OR (or use “is one of”). Different fields → AND.
        </p>
        <div v-if="!filterFieldOptions.length" class="tw-insight__muted">
          This campaign has no products with fields to filter on.
        </div>
        <template v-else>
          <div v-if="filters.length" class="tw-adv-filters">
            <div
              v-for="(filter, index) in filters"
              :key="filter.id"
              class="tw-adv-filter-row"
              :class="{ 'tw-adv-filter-row--range': isRangeOperator(filter.operator) }"
            >
              <v-combobox
                v-model="filter.field"
                :items="filterFieldOptions"
                item-title="label"
                item-value="key"
                placeholder="Field (e.g. nationality)"
                variant="outlined"
                density="comfortable"
                hide-details
                class="tw-adv-filter-field"
                :disabled="disabled"
                @update:model-value="onFilterFieldChange(filter)"
              />
              <v-select
                v-model="filter.operator"
                :items="operatorsForRow(filter)"
                item-title="title"
                item-value="value"
                variant="outlined"
                density="comfortable"
                hide-details
                class="tw-adv-filter-operator"
                :disabled="disabled"
                @update:model-value="onFilterOperatorChange(filter)"
              />

              <div v-if="isRangeOperator(filter.operator)" class="tw-adv-filter-range">
                <v-text-field
                  v-if="filterRowKind(filter) === 'date'"
                  :model-value="getFilterDateInputValue(getFilterRangeMin(filter))"
                  type="date"
                  placeholder="From"
                  variant="outlined"
                  density="comfortable"
                  hide-details
                  :disabled="disabled"
                  @update:model-value="setFilterRangeMin(filter, $event)"
                />
                <v-text-field
                  v-else
                  :model-value="getFilterRangeMin(filter)"
                  type="number"
                  placeholder="Min"
                  variant="outlined"
                  density="comfortable"
                  hide-details
                  :disabled="disabled"
                  @update:model-value="setFilterRangeMin(filter, $event)"
                />
                <v-text-field
                  v-if="filterRowKind(filter) === 'date'"
                  :model-value="getFilterDateInputValue(getFilterRangeMax(filter))"
                  type="date"
                  placeholder="To"
                  variant="outlined"
                  density="comfortable"
                  hide-details
                  :disabled="disabled"
                  @update:model-value="setFilterRangeMax(filter, $event)"
                />
                <v-text-field
                  v-else
                  :model-value="getFilterRangeMax(filter)"
                  type="number"
                  placeholder="Max"
                  variant="outlined"
                  density="comfortable"
                  hide-details
                  :disabled="disabled"
                  @update:model-value="setFilterRangeMax(filter, $event)"
                />
              </div>

              <v-text-field
                v-else-if="isDateCompareOperator(filter.operator) || (filter.operator === 'EQUALS' && filterRowKind(filter) === 'date')"
                :model-value="getFilterDateInputValue(getFilterSingleValue(filter))"
                type="date"
                variant="outlined"
                density="comfortable"
                hide-details
                class="tw-adv-filter-values"
                :disabled="disabled"
                @update:model-value="setFilterSingleValue(filter, $event)"
              />

              <v-text-field
                v-else-if="isNumericCompareOperator(filter.operator) || (filter.operator === 'EQUALS' && filterRowKind(filter) === 'number')"
                :model-value="getFilterSingleValue(filter)"
                type="number"
                placeholder="Number"
                variant="outlined"
                density="comfortable"
                hide-details
                class="tw-adv-filter-values"
                :disabled="disabled"
                @update:model-value="setFilterSingleValue(filter, $event)"
              />

              <v-combobox
                v-else-if="filter.operator === 'IN'"
                v-model="filter.values"
                :items="filterValueOptions(filter)"
                placeholder="Values, press Enter"
                variant="outlined"
                density="comfortable"
                hide-details
                multiple
                chips
                closable-chips
                class="tw-adv-filter-values"
                :disabled="disabled"
              />

              <v-combobox
                v-else
                :model-value="getFilterSingleValue(filter)"
                :items="filterValueOptions(filter)"
                :placeholder="filterValuesPlaceholder(filter)"
                variant="outlined"
                density="comfortable"
                hide-details
                class="tw-adv-filter-values"
                :disabled="disabled"
                @update:model-value="setFilterSingleValue(filter, $event)"
              />

              <v-btn
                icon
                variant="text"
                size="small"
                aria-label="Remove filter"
                :disabled="disabled"
                @click="removeFilterRow(index)"
              >
                <v-icon size="18">mdi-close</v-icon>
              </v-btn>
            </div>
          </div>
          <v-btn
            variant="outlined"
            size="small"
            class="text-none mt-2"
            prepend-icon="mdi-filter-plus-outline"
            :disabled="disabled"
            @click="addFilterRow"
          >
            {{ filters.length ? 'Add filter' : 'Add your first filter' }}
          </v-btn>
        </template>
      </div>
    </section>

    <section class="tw-section">
      <div class="tw-section__head">
        <span class="tw-section__title">Lead distribution</span>
      </div>
      <div class="tw-section__body tw-form-grid">
        <label>
          <span class="tw-field-label">How leads are split</span>
          <select v-model="form.distribution_mode" class="tw-select" :disabled="disabled">
            <option v-for="opt in distributionModeOptions" :key="opt.value" :value="opt.value">
              {{ opt.title }}
            </option>
          </select>
        </label>

        <label v-if="isFieldBasedMode" class="tw-form-grid__full">
          <span class="tw-field-label">Agent field</span>
          <v-combobox
            v-model="form.agent_field"
            :items="agentFieldSuggestions"
            placeholder="e.g. assigned_agent_email"
            variant="outlined"
            density="comfortable"
            hide-details
            :disabled="disabled"
          />
          <p class="tw-insight__muted tw-insight__muted--tight">
            Each lead's value in this product field must be an assigned agent's email. Leads with
            an empty field or an email not in the campaign's agent list are reported as unmatched.
          </p>
        </label>

        <div v-if="isExcelMode" class="tw-form-grid__full">
          <span class="tw-field-label">Assignment spreadsheet</span>
          <v-file-input
            v-model="excelFile"
            accept=".xlsx,.xls,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv"
            placeholder="Upload .xlsx / .xls / .csv"
            prepend-icon=""
            prepend-inner-icon="mdi-file-excel-outline"
            variant="outlined"
            density="comfortable"
            hide-details
            clearable
            show-size
            :disabled="disabled || parsingExcel || assignmentPhase !== 'idle'"
            :loading="parsingExcel"
            @update:model-value="onExcelFileChange"
          />
          <p class="tw-insight__muted tw-insight__muted--tight">
            Columns: <strong>unique_identifier</strong> (matched to each product's identifier
            field) and <strong>agent_email</strong> (must be one of the agents assigned to this
            campaign). Upload a fresh file whenever this changes -- merge-only, existing leads
            are kept.
          </p>
          <p v-if="excelAssignments.length" class="tw-adv-note tw-adv-note--ok">
            {{ excelAssignments.length }} assignment
            {{ excelAssignments.length === 1 ? 'row' : 'rows' }} ready.
          </p>
          <p v-if="excelParseError" class="tw-adv-note tw-adv-note--error">{{ excelParseError }}</p>
        </div>

        <div
          v-if="assignmentPhase === 'uploading' || assignmentPhase === 'processing'"
          class="tw-form-grid__full tw-adv-processing"
        >
          <v-progress-circular indeterminate size="20" width="2" color="primary" />
          <span>
            {{ assignmentPhase === 'uploading' ? 'Uploading assignments…' : 'Adding newly-matching leads in the background…' }}
          </span>
        </div>

        <div v-if="unmatchedAssignments.length" class="tw-form-grid__full">
          <p class="tw-field-label">
            Unmatched leads
            <span v-if="assignmentMatchSummary" style="font-weight: 400">({{ assignmentMatchSummary }})</span>
          </p>
          <div class="tw-adv-unmatched">
            <div class="tw-adv-unmatched__row tw-adv-unmatched__row--head">
              <span>Identifier</span><span>Agent email</span><span>Reason</span>
            </div>
            <div
              v-for="(row, idx) in unmatchedAssignments"
              :key="`${row.unique_identifier}-${row.agent_email}-${idx}`"
              class="tw-adv-unmatched__row"
            >
              <span>{{ row.unique_identifier || '—' }}</span>
              <span>{{ row.agent_email || '—' }}</span>
              <span>{{ row.reason || '—' }}</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section v-if="isAiCampaign" class="tw-section">
      <div class="tw-section__head">
        <span class="tw-section__title">Call script</span>
      </div>
      <div class="tw-section__body tw-form-grid">
        <!-- Greeting/Mission drive the live conversational agent -- a
        Static voice campaign never has a conversation (it plays a fixed
        message, configured in the Voice mode section below, and hangs up),
        so there's no personality or opening line for these to shape. And the
        org's one default-inbound campaign is answered by EC2 using the
        org-level system prompt set in the BMS, never this campaign's own
        greeting/mission -- editing them here would silently do nothing. -->
        <label v-if="!hideCallScript" class="tw-form-grid__full">
          <span class="tw-field-label">Greeting</span>
          <VariableInsertTextarea
            v-model="form.greeting"
            :variables="callScriptVariables"
            :rows="2"
            :disabled="disabled"
          />
          <p v-if="greetingMatchesOrgDefault" class="tw-field-warning">
            This still matches your organization's default greeting -- write one
            specific to what THIS campaign is actually about, or callers may hear
            something confusing or out of context.
          </p>
        </label>
        <label v-if="!hideCallScript" class="tw-form-grid__full">
          <span class="tw-field-label">Mission</span>
          <VariableInsertTextarea
            v-model="form.mission_prompt"
            :variables="callScriptVariables"
            :rows="4"
            :disabled="disabled"
          />
        </label>
        <label>
          <span class="tw-field-label">Recording disclaimer</span>
          <select v-model="disclaimerSelect" class="tw-select" :disabled="disabled">
            <option value="default">Organisation default</option>
            <option value="never">Never include</option>
          </select>
        </label>
      </div>
    </section>

    <section v-if="isAiCampaign" class="tw-section">
      <div class="tw-section__head">
        <span class="tw-section__title">Voice mode</span>
      </div>
      <div class="tw-section__body tw-form-grid">
        <label>
          <span class="tw-field-label">Who speaks on the call</span>
          <select v-model="form.voice_mode" class="tw-select" :disabled="disabled">
            <option value="AI_SPEAK">AI speaks (conversation)</option>
            <option value="STATIC_VOICE">Static voice (play a message, then hang up)</option>
          </select>
        </label>

        <template v-if="isStaticVoice">
          <label>
            <span class="tw-field-label">Message source</span>
            <select v-model="form.voice_source" class="tw-select" :disabled="disabled">
              <option value="POLLY_TEXT">Type a message (text-to-speech)</option>
              <option value="UPLOADED_AUDIO">Upload a recorded .wav</option>
            </select>
          </label>

          <label v-if="form.voice_source === 'POLLY_TEXT'">
            <span class="tw-field-label">Language</span>
            <select v-model="form.voice_language" class="tw-select" :disabled="disabled">
              <option v-for="lang in VOICE_LANGUAGES" :key="lang.value" :value="lang.value">
                {{ lang.label }}
              </option>
            </select>
            <p class="tw-field-hint">
              Write the message itself in this language below -- the voice matches whatever
              script it's given.
            </p>
          </label>

          <label>
            <span class="tw-field-label">Personalization</span>
            <select
              v-model="form.voice_personalization"
              class="tw-select"
              :disabled="disabled || form.voice_source === 'UPLOADED_AUDIO'"
            >
              <option value="SAME_FOR_ALL">Same message for everyone</option>
              <option value="PER_LEAD">Personalized per lead</option>
            </select>
            <p v-if="form.voice_source === 'UPLOADED_AUDIO'" class="tw-field-hint">
              A single uploaded recording can't have a lead's name spliced in -- everyone hears the
              same file. Switch the message source to "Type a message" to personalize per lead.
            </p>
          </label>

          <label v-if="form.voice_source === 'POLLY_TEXT'" class="tw-form-grid__full">
            <span class="tw-field-label">Message</span>
            <VariableInsertTextarea
              v-model="form.voice_script_text"
              :variables="form.voice_personalization === 'PER_LEAD' ? voiceScriptVariables : []"
              :rows="4"
              maxlength="2000"
              :disabled="disabled"
            />
            <p v-if="form.voice_personalization !== 'PER_LEAD'" class="tw-insight__muted tw-insight__muted--tight">
              Plays the same message to every lead. Set Personalization to "Personalized per
              lead" to insert a variable here instead.
            </p>
          </label>

          <div v-else class="tw-form-grid__full">
            <span class="tw-field-label">Audio file</span>
            <v-file-input
              v-model="voiceAudioFile"
              accept=".wav,audio/wav"
              placeholder="Upload .wav"
              prepend-icon=""
              prepend-inner-icon="mdi-file-music-outline"
              variant="outlined"
              density="comfortable"
              hide-details
              clearable
              show-size
              :disabled="disabled || voiceAudioUploading"
              :loading="voiceAudioUploading"
              @update:model-value="onVoiceAudioFileChange"
            />
            <p v-if="form.voice_audio_filename" class="tw-adv-note tw-adv-note--ok">
              Current file: {{ form.voice_audio_filename }}
            </p>
            <p v-if="voiceAudioUploadError" class="tw-adv-note tw-adv-note--error">
              {{ voiceAudioUploadError }}
            </p>
          </div>

          <div class="tw-form-grid__full tw-voice-preview">
            <v-btn
              variant="outlined"
              size="small"
              class="text-none"
              prepend-icon="mdi-play-circle-outline"
              :loading="voicePreviewLoading"
              :disabled="disabled || !canPreviewVoice"
              @click="previewVoice"
            >
              Preview message
            </v-btn>
            <p v-if="!canPreviewVoice" class="tw-insight__muted tw-insight__muted--tight">
              Save these settings first -- preview always plays back what's saved, not unsaved edits above.
            </p>
            <p v-else-if="form.voice_personalization === 'PER_LEAD'" class="tw-insight__muted tw-insight__muted--tight">
              Uses sample values (e.g. "Alex", "Riya") in place of <code>{contact_name}</code>/<code>{agent_name}</code>/lead fields -- a real call uses the actual lead's data.
            </p>
            <audio
              v-if="voicePreviewUrl"
              :src="voicePreviewUrl"
              controls
              class="tw-voice-preview__player"
            />
            <p v-if="voicePreviewError" class="tw-adv-note tw-adv-note--error">{{ voicePreviewError }}</p>
          </div>
        </template>
      </div>
    </section>

    <section v-if="isAiCampaign && !isStaticVoice" class="tw-section">
      <div class="tw-section__head">
        <span class="tw-section__title">AI handoff</span>
      </div>
      <div class="tw-section__body tw-form-grid">
        <div class="tw-form-grid__full">
          <v-switch
            :model-value="form.ai_transfer_eligible"
            label="Available as an AI handoff target"
            color="primary"
            density="comfortable"
            hide-details
            :disabled="disabled"
            @update:model-value="(value) => (form.ai_transfer_eligible = value)"
          />
          <p class="tw-field-hint">
            When on, any other AI campaign in this org can hand a live call to this
            one mid-conversation -- your receptionist AI, or another specialist
            campaign, can transfer a caller here when their need matches what this
            campaign handles. The caller stays on the line; a human transfer is
            never involved.
          </p>
        </div>
      </div>
    </section>

    <section v-if="isAiCampaign" class="tw-section">
      <div class="tw-section__head">
        <span class="tw-section__title">Capture caller details</span>
      </div>
      <div class="tw-section__body tw-form-grid">
        <p class="tw-form-grid__full tw-field-hint">
          Save specific details the caller mentions onto their own record -- works on any call this
          campaign handles. Slot booking (its own tab) picks which one of these fields captures the
          preferred date/time, so there's nothing booking-specific to configure here.
        </p>
        <div class="tw-form-grid__full tw-capture-fields">
          <div v-for="(row, i) in form.capture_lead_fields" :key="i" class="tw-capture-fields__row">
            <select v-model="row.field" class="tw-select" :disabled="disabled">
              <option value="">Choose a field…</option>
              <option v-for="opt in leadFieldOptions" :key="opt.key" :value="opt.key">{{ opt.label }}</option>
            </select>
            <input
              v-model="row.prompt"
              type="text"
              class="tw-input"
              placeholder="What should the AI ask for? e.g. Ask why they're coming in"
              maxlength="300"
              :disabled="disabled"
            />
            <input
              v-model="row.description"
              type="text"
              class="tw-input"
              placeholder="Short label for this field, e.g. Reason for Visit"
              maxlength="80"
              :disabled="disabled"
            />
            <v-btn
              icon
              variant="text"
              size="small"
              aria-label="Remove field"
              :disabled="disabled"
              @click="removeCaptureLeadFieldRow(i)"
            >
              <v-icon size="18">mdi-close</v-icon>
            </v-btn>
          </div>
          <v-btn
            variant="outlined"
            size="small"
            class="text-none mt-2"
            prepend-icon="mdi-plus"
            :disabled="disabled"
            @click="addCaptureLeadFieldRow"
          >
            {{ form.capture_lead_fields.length ? 'Add another field' : 'Add a field to capture' }}
          </v-btn>
        </div>
      </div>
    </section>

    <section class="tw-section">
      <div class="tw-section__head">
        <span class="tw-section__title">Auto-dial</span>
      </div>
      <div class="tw-section__body tw-form-grid">
        <div class="tw-form-grid__full tw-dialing-summary" aria-live="polite">
          <v-icon icon="mdi-phone-message-outline" size="16" class="tw-dialing-summary__icon" />
          <p class="tw-dialing-summary__text">{{ dialingSummary }}</p>
        </div>
        <div class="tw-form-grid__full">
          <v-switch
            :model-value="form.auto_dial_enabled"
            :label="isAiCampaign ? 'Auto-dial during working hours' : 'Softphone auto-dial during working hours'"
            color="primary"
            density="comfortable"
            hide-details
            :disabled="disabled || (isAiCampaign && isRecurringReminder)"
            @update:model-value="onAutoDialToggle"
          />
          <p v-if="isAiCampaign && isRecurringReminder" class="tw-field-hint">
            Always on for a recurring reminder -- without it, leads would come due on
            schedule and just sit there, never actually called.
          </p>
        </div>

        <div v-if="isAiCampaign" class="tw-form-grid__full">
          <v-switch
            v-model="form.restrict_to_known_callers"
            label="Only dial numbers already in the Callers list"
            color="primary"
            density="comfortable"
            hide-details
            :disabled="disabled"
          />
          <p class="tw-field-hint">
            When on, this campaign skips any lead whose number isn't already known in
            the org's Callers module, instead of dialing everyone in the lead list.
          </p>
        </div>

        <template v-if="form.auto_dial_enabled">
          <label>
            <span class="tw-field-label">Working hours start</span>
            <input v-model="form.working_hours_start" type="time" class="tw-select" :disabled="disabled" />
          </label>
          <label>
            <span class="tw-field-label">Working hours end</span>
            <input v-model="form.working_hours_end" type="time" class="tw-select" :disabled="disabled" />
          </label>
        </template>

        <!-- NOT inside the auto-dial-only template above, unlike working
             hours -- this spacing protects the person being called, not the
             scheduler, so it applies just as much to a manually-run
             (auto-dial off / Voice Blast) campaign every time someone clicks
             "Run AI calls" as it does to an auto-dial tick. Used to be
             hidden here (and force-nulled on save) whenever auto-dial was
             off, which made the platform floor of 15 minutes invisible AND
             unconfigurable for exactly those campaigns -- same shape of gap
             max_dial_attempts below was already fixed for. Confirmed live
             2026-08-31 (Prezence Solution's "Closed circle BNI members day"
             campaign, auto-dial off, retry_cooldown_minutes stuck at null
             with no field to see or raise it). -->
        <label>
          <span class="tw-field-label">Retry cooldown (minutes, optional)</span>
          <input v-model="form.retry_cooldown_minutes" type="number" min="1" step="1" class="tw-input" placeholder="Platform floor (15)" :disabled="disabled" />
          <p class="tw-field-hint">
            Minimum wait before redialing the same lead again. Applies whether calls are
            placed by auto-dial or one at a time via "Run AI calls" -- leave blank to use
            the platform floor of 15 minutes. Any value set is still raised to that floor
            if it's set lower.
          </p>
        </label>

        <!-- NOT inside the auto-dial-only template above, unlike working
             hours/retry cooldown -- this cap protects the person being
             called, not the scheduler, so it applies just as much to a
             manually-run (auto-dial off / Voice Blast) campaign every time
             someone clicks "Run AI calls". Used to be hidden here whenever
             auto-dial was off, which made the platform default of 3 attempts
             invisible AND unconfigurable for exactly those campaigns.
             Confirmed live 2026-08-26 (Gopika Onam Bosch VB, leads stuck at
             "Reached max dial attempts (3/3)" with no field to raise it). -->
        <label>
          <span class="tw-field-label">Max dial attempts (optional)</span>
          <input v-model="form.max_dial_attempts" type="number" min="1" step="1" class="tw-input" placeholder="Platform default (3)" :disabled="disabled" />
          <p class="tw-field-hint">
            Max times this campaign will ever redial the same lead before giving up on them.
            Applies whether calls are placed by auto-dial or one at a time -- leave blank to
            use the platform default of 3.
          </p>
        </label>

        <label>
          <span class="tw-field-label">Daily dial cap override (optional)</span>
          <input
            v-model="form.daily_dial_cap"
            type="number"
            min="1"
            step="1"
            class="tw-input"
            placeholder="Platform default"
            :disabled="disabled"
          />
          <p class="tw-field-hint">
            Pre-filled from the product's own default when it has one -- edit it to set this
            campaign's own value instead. Max times a lead can be dialed in one day. Applies whether calls
            are placed by auto-dial or one at a time -- leave blank to use the product's own default.
          </p>
        </label>
      </div>
    </section>

    <section class="tw-section">
      <div class="tw-section__head">
        <span class="tw-section__title">Adoption behavior</span>
      </div>
      <div class="tw-section__body">
        <v-switch
          v-model="form.auto_adopt_matching_leads"
          label="Auto-adopt matching leads from other campaigns"
          color="primary"
          density="comfortable"
          hide-details
          :disabled="disabled"
        />
        <p class="tw-insight__muted tw-insight__muted--tight">
          When a lead is created (or re-submitted) elsewhere on the same product and matches this
          campaign's own products and filters, it's automatically added here too.
        </p>
        <v-switch
          v-model="form.only_future_leads"
          label="Only future leads on the next save"
          color="primary"
          density="comfortable"
          hide-details
          class="mt-3"
          :disabled="disabled"
        />
        <p class="tw-insight__muted tw-insight__muted--tight">
          Only takes effect on a save that itself changes lead distribution, products, or filters --
          skips pulling in leads that already match right now.
        </p>
      </div>
    </section>

    <div class="tw-adv-settings__footer">
      <v-btn
        color="primary"
        variant="flat"
        class="text-none"
        :loading="saving"
        :disabled="disabled"
        @click="submit"
      >
        Save settings
      </v-btn>
    </div>
  </div>

  <v-dialog v-model="showAutoDialConfirm" max-width="440" persistent>
    <v-card>
      <v-card-title class="text-h6">Turn on auto-dial?</v-card-title>
      <v-card-text>
        This campaign will start calling every matching lead automatically, within working
        hours, as soon as you save -- not just once, on a repeating cycle until each lead is
        worked or the campaign ends. Make sure the script, schedule and lead list are exactly
        what you want first.
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn variant="text" class="text-none" @click="cancelAutoDialToggle">Cancel</v-btn>
        <v-btn color="primary" variant="flat" class="text-none" @click="confirmAutoDialToggle">
          Turn on auto-dial
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { computed, nextTick, ref, watch } from 'vue'
import { useStore } from 'vuex'
import {
  editCampaign,
  putCampaignAssignmentCsv,
  fetchCampaignAssignmentUploadStatus,
  fetchTaskVoiceAudioUploadUrl,
  fetchTaskVoiceAudioPreviewUrl,
  putCampaignVoiceAudio,
} from '@/api/campaignsApi'
import { getApiMessage, isApiSuccess } from '@/mixins/Extras/ApiResponse'
import { unwrapApiPayload } from '@/utils/graphql'
import { CAMPAIGN_DISTRIBUTION_MODES, CAMPAIGN_HANDLED_BY_OPTIONS } from '@/utils/campaigns'
import { isAiAgent, resolveListAgentType } from '@/utils/orgUsers'
import { readGreetingMessageFromOrg } from '@/utils/agentOtherSettings'
import { epochToDateInputValue } from '@/utils/leadFieldTypes'
import {
  normalizeAssignmentUploadStatus,
  normalizeUnmatchedAssignments,
  parseExcelAssignmentsFile,
} from '@/utils/excelAssignments'
import {
  buildAgentFieldSuggestions,
  buildCampaignFilterFieldOptions,
  campaignFilterFieldKind,
  createEmptyCampaignFilter,
  findCampaignFilterFieldDef,
  getFilterDateInputValue,
  getFilterRangeMax,
  getFilterRangeMin,
  getFilterSingleValue,
  hydrateCampaignFiltersFromApi,
  normalizeCampaignFiltersForApi,
  operatorsForFilterField,
  setFilterRangeMax,
  setFilterRangeMin,
  setFilterSingleValue,
} from '@/utils/campaignFilters'
import FieldWithLeadOverride from '@/components/Campaigns/FieldWithLeadOverride.vue'
import VariableInsertTextarea from '@/components/Shared/VariableInsertTextarea.vue'

defineOptions({ name: 'CampaignAdvancedSettings' })

const props = defineProps({
  campaign: { type: Object, default: null },
  /** This campaign's own products (already filtered to campaign.contextNames). */
  contexts: { type: Array, default: () => [] },
  /** Every product in the org -- unfiltered, for the Product picker below (contexts above stays scoped to the campaign's current selection, for filter-field building). */
  allContexts: { type: Array, default: () => [] },
  /** Full org agent list -- narrowed internally to campaign.assignedAgentIds. */
  agents: { type: Array, default: () => [] },
  disabled: { type: Boolean, default: false },
})

const emit = defineEmits(['saved', 'error'])

const store = useStore()

const saving = ref(false)
const filters = ref([])
// Guards the handled_by watcher below (clears the agent selection on a
// user-driven AI/human flip) so it doesn't also fire -- and wipe out the
// just-loaded agents -- while hydrate() below is replacing form.value
// wholesale for a freshly-loaded campaign. Same reasoning/pattern as
// CreateCampaignDialog.vue's own `hydrating` guard.
let hydratingCampaign = false

function toDateInputValue(value) {
  const text = String(value ?? '').trim()
  if (!text) return ''
  if (/^\d{4}-\d{2}-\d{2}/.test(text)) return text.slice(0, 10)
  return epochToDateInputValue(value) || ''
}

function defaultForm() {
  return {
    task_name: '',
    description: '',
    task_type: 'STANDARD',
    context_names: [],
    start_date: '',
    end_date: '',
    anchor_field: '',
    anchor_date: '',
    schedule_type: 'DAILY',
    schedule_type_field: '',
    schedule_frequency: null,
    schedule_frequency_field: '',
    schedule_time: '',
    schedule_time_field: '',
    schedule_day_of_week: '',
    schedule_day_of_week_field: '',
    schedule_day_of_month: '',
    schedule_day_of_month_field: '',
    handled_by: 'HUMAN_AGENT',
    assigned_agent_ids: [],
    redirect_targets: [],
    redirect_message: '',
    redirect_description: '',
    distribution_mode: 'OPEN',
    agent_field: '',
    greeting: '',
    mission_prompt: '',
    disclaimer_enabled: null,
    voice_mode: 'AI_SPEAK',
    voice_source: 'POLLY_TEXT',
    voice_personalization: 'SAME_FOR_ALL',
    voice_script_text: '',
    voice_audio_raw_key: '',
    voice_audio_filename: '',
    voice_language: 'en-IN',
    ai_transfer_eligible: false,
    capture_datetime_field: '',
    capture_datetime_time_field: '',
    capture_datetime_prompt: '',
    capture_lead_fields: [],
    auto_dial_enabled: false,
    restrict_to_known_callers: false,
    working_hours_start: '',
    working_hours_end: '',
    retry_cooldown_minutes: '',
    max_dial_attempts: '',
    daily_dial_cap: '',
    auto_adopt_matching_leads: true,
    only_future_leads: false,
  }
}
const form = ref(defaultForm())

// 2026-09-07: briefly tried merging "Capture agreed time" into this list
// via an is_agreed_time checkbox per row -- reverted same day per Ramu's
// own correction: this list should stay purely "what does the AI capture",
// with zero booking-specific concepts in it. Slot booking (its own tab)
// now owns picking WHICH already-captured field is the agreed time
// instead, via a simple selector reading straight off this same
// capture_lead_fields array -- see CampaignSlotBooking.vue.
function addCaptureLeadFieldRow() {
  form.value.capture_lead_fields = [...form.value.capture_lead_fields, { field: '', prompt: '', description: '' }]
}
function removeCaptureLeadFieldRow(index) {
  form.value.capture_lead_fields = form.value.capture_lead_fields.filter((_, i) => i !== index)
}

const disclaimerSelect = computed({
  get: () => (form.value.disclaimer_enabled === false ? 'never' : 'default'),
  set: (v) => { form.value.disclaimer_enabled = v === 'never' ? false : null },
})

// Lowest configured daily_dial_cap among this campaign's own products, so a
// campaign spanning several products never suggests a looser number than
// its most restrictive one. null when none of them have set one -- nothing
// concrete to pre-fill with in that case (the platform's own hardcoded
// fallback isn't "the product's value", so it's not shown as if it were).
const productDailyDialCapDefault = computed(() => {
  const names = new Set(
    (Array.isArray(props.campaign?.contextNames) ? props.campaign.contextNames : [])
      .map((name) => String(name ?? '').trim().toLowerCase())
      .filter(Boolean),
  )
  const values = (props.contexts || [])
    .filter((c) => names.has(String(c.context_name ?? c.contextName ?? '').trim().toLowerCase()))
    .map((c) => Number(c.daily_dial_cap ?? c.dailyDialCap))
    .filter((n) => Number.isInteger(n) && n > 0)
  return values.length ? Math.min(...values) : null
})

// Platform-level dialing guardrails, mirrored from their one true source in
// the backend (yandle_admin_portal_1/index.mjs: DEFAULT_MAX_DIAL_ATTEMPTS,
// MAX_DIALS_PER_LEAD_PER_DAY, ONE_TIME_RETRY_WINDOW_MINUTES,
// ONE_TIME_MAX_ATTEMPTS_CEILING) purely so dialingSummary below can describe
// what an UNSET field actually resolves to. These are display text only --
// every real limit is still computed and enforced server-side regardless of
// what this component shows, so a drift here is a wrong sentence, never a
// wrong dial.
const PLATFORM_DEFAULT_MAX_DIAL_ATTEMPTS = 3
const PLATFORM_MAX_DIALS_PER_LEAD_PER_DAY = 4
const PLATFORM_ONE_TIME_RETRY_WINDOW_HOURS = 3
const PLATFORM_ONE_TIME_MAX_ATTEMPTS_CEILING = 12
const PLATFORM_MIN_RETRY_COOLDOWN_MINUTES = 15

// A live, plain-English readout of what this campaign's current dialing
// settings actually DO -- built after Ramu's own "I keep forgetting what
// these settings mean, and I'm the developer" feedback (2026-08-26). The
// fields here (auto-dial, working hours, retry cooldown, max dial attempts,
// daily dial cap) each independently make sense in isolation, but knowing
// what they add up to for a real phone call requires holding all of them
// (plus which ones even apply to this campaign's type) in your head at
// once -- this sentence does that instead, so nobody has to.
//
// Three genuinely different shapes, matching runTaskAICalls' own eligibility
// branches (index.mjs ~line 13526-13568) exactly -- getting this wrong would
// make the settings LESS trustworthy, not more, so each branch mirrors the
// real enforcement logic rather than a simplified guess at it:
//   1. A RECURRING_REMINDER with schedule_type ONE_TIME (e.g. "remind once
//      at this booked slot") -- max_dial_attempts is not even consulted
//      here; a fixed 3-hour window and a hardcoded ceiling of 12 govern
//      instead (see the "oneTimeCycle" branch there).
//   2. A RECURRING_REMINDER on a real cadence (YEARLY/MONTHLY/WEEKLY/DAILY)
//      -- max_dial_attempts applies, but PER CYCLE: reArmDueReminders resets
//      dial_attempt_count to 0 every time it advances to the next occurrence,
//      so the count is never a lifetime total for these.
//   3. Everything else (STANDARD campaigns, any handled_by, auto-dial on or
//      off) -- max_dial_attempts is a real lifetime-per-lead ceiling.
const dialingSummary = computed(() => {
  const maxAttempts = Number(form.value.max_dial_attempts) > 0
    ? Number(form.value.max_dial_attempts)
    : PLATFORM_DEFAULT_MAX_DIAL_ATTEMPTS
  const dailyCap = Number(form.value.daily_dial_cap) > 0
    ? Number(form.value.daily_dial_cap)
    : (productDailyDialCapDefault.value || PLATFORM_MAX_DIALS_PER_LEAD_PER_DAY)
  // Always a real number now, never null -- retry_cooldown_minutes applies
  // regardless of auto-dial (see the field's own hint text), and
  // effectiveRetryCooldownMinutes server-side never lets the effective
  // value drop below the platform floor either way, blank or explicit.
  const cooldown = Math.max(
    Number(form.value.retry_cooldown_minutes) > 0 ? Number(form.value.retry_cooldown_minutes) : 0,
    PLATFORM_MIN_RETRY_COOLDOWN_MINUTES,
  )
  const times = (n) => `${n} time${n === 1 ? '' : 's'}`
  const dailyCapClause = `won't call the same person more than ${times(dailyCap)} a day`
  const hours = form.value.working_hours_start && form.value.working_hours_end
    ? ` between ${form.value.working_hours_start} and ${form.value.working_hours_end}`
    : ''

  if (isRecurringReminder.value && isOneTimeSchedule.value) {
    return `Auto-dial is always on for this reminder. Once it comes due, it keeps` +
      `${cooldown ? ` trying every ${cooldown} minutes` : ' trying'} for up to` +
      ` ${PLATFORM_ONE_TIME_RETRY_WINDOW_HOURS} hours (at most ${PLATFORM_ONE_TIME_MAX_ATTEMPTS_CEILING} attempts),` +
      ` then gives up until the next scheduled reminder. It ${dailyCapClause}.` +
      ` "Max dial attempts" below isn't used for this schedule type -- the 3-hour window governs instead.`
  }
  if (isRecurringReminder.value) {
    return `Auto-dial is always on for this reminder${hours}. Each time it comes due, it tries up to` +
      ` ${times(maxAttempts)}${cooldown ? `, waiting at least ${cooldown} minutes between tries` : ''},` +
      ` then starts that count over for the next occurrence. It ${dailyCapClause}.`
  }
  if (form.value.auto_dial_enabled) {
    return `Auto-dial is on: calls go out automatically${hours}. Each lead gets up to` +
      ` ${times(maxAttempts)} total${cooldown ? `, waiting at least ${cooldown} minutes between tries` : ''}.` +
      ` It ${dailyCapClause}.`
  }
  const manualTrigger = isAiCampaign.value ? 'someone clicks "Run AI calls"' : 'an agent dials them from the softphone'
  return `Auto-dial is off -- calls only go out when ${manualTrigger}. Each lead gets up to` +
    ` ${times(maxAttempts)} total across every run, waiting at least ${cooldown} minutes` +
    ` between tries. It ${dailyCapClause}.`
})

function hydrate(campaign) {
  if (!campaign) {
    form.value = defaultForm()
    filters.value = []
    return
  }
  form.value = {
    task_name: String(campaign.name ?? '').trim(),
    description: String(campaign.description ?? '').trim(),
    task_type: String(campaign.taskType ?? 'STANDARD').toUpperCase() || 'STANDARD',
    context_names: Array.isArray(campaign.contextNames) ? [...campaign.contextNames] : [],
    start_date: toDateInputValue(campaign.startDate),
    end_date: toDateInputValue(campaign.endDate),
    anchor_field: String(campaign.anchorField ?? '').trim(),
    anchor_date: toDateInputValue(campaign.anchorDate),
    schedule_type: String(campaign.scheduleType ?? '').trim().toUpperCase() || 'DAILY',
    schedule_type_field: String(campaign.scheduleTypeField ?? '').trim(),
    schedule_frequency: campaign.scheduleFrequency != null ? Number(campaign.scheduleFrequency) : null,
    schedule_frequency_field: String(campaign.scheduleFrequencyField ?? '').trim(),
    schedule_time: String(campaign.scheduleTime ?? '').trim(),
    schedule_time_field: String(campaign.scheduleTimeField ?? '').trim(),
    schedule_day_of_week: String(campaign.scheduleDayOfWeek ?? '').trim().toUpperCase(),
    schedule_day_of_week_field: String(campaign.scheduleDayOfWeekField ?? '').trim(),
    schedule_day_of_month: String(campaign.scheduleDayOfMonth ?? '').trim(),
    schedule_day_of_month_field: String(campaign.scheduleDayOfMonthField ?? '').trim(),
    handled_by: String(campaign.handledBy ?? 'HUMAN_AGENT').toUpperCase() || 'HUMAN_AGENT',
    assigned_agent_ids: Array.isArray(campaign.assignedAgentIds) ? [...campaign.assignedAgentIds] : [],
    redirect_targets: Array.isArray(campaign.redirectTargets) ? [...campaign.redirectTargets] : [],
    redirect_message: String(campaign.redirectMessage ?? '').trim(),
    redirect_description: String(campaign.redirectDescription ?? '').trim(),
    distribution_mode: String(campaign.distributionMode ?? 'OPEN').toUpperCase() || 'OPEN',
    agent_field: String(campaign.agentField ?? '').trim(),
    greeting: String(campaign.greeting ?? '').trim(),
    mission_prompt: String(campaign.missionPrompt ?? '').trim(),
    disclaimer_enabled: campaign.disclaimerEnabled ?? null,
    voice_mode: String(campaign.voiceMode ?? 'AI_SPEAK').toUpperCase() || 'AI_SPEAK',
    voice_source: String(campaign.voiceSource ?? '').toUpperCase() || 'POLLY_TEXT',
    voice_personalization: String(campaign.voicePersonalization ?? '').toUpperCase() || 'SAME_FOR_ALL',
    voice_script_text: String(campaign.voiceScriptText ?? '').trim(),
    voice_audio_raw_key: String(campaign.voiceAudioRawKey ?? '').trim(),
    voice_audio_filename: String(campaign.voiceAudioFilename ?? '').trim(),
    voice_language: String(campaign.voiceLanguage ?? '').trim() || 'en-IN',
    ai_transfer_eligible: Boolean(campaign.aiTransferEligible),
    capture_datetime_field: String(campaign.captureDatetimeField ?? '').trim(),
    capture_datetime_time_field: String(campaign.captureDatetimeTimeField ?? '').trim(),
    capture_datetime_prompt: String(campaign.captureDatetimePrompt ?? '').trim(),
    capture_lead_fields: Array.isArray(campaign.captureLeadFields)
      ? campaign.captureLeadFields.map((f) => ({ field: String(f?.field ?? ''), prompt: String(f?.prompt ?? ''), description: String(f?.description ?? '') }))
      : [],
    auto_dial_enabled: Boolean(campaign.autoDialEnabled),
    restrict_to_known_callers: Boolean(campaign.restrictToKnownCallers),
    working_hours_start: String(campaign.workingHoursStart ?? '').trim(),
    working_hours_end: String(campaign.workingHoursEnd ?? '').trim(),
    retry_cooldown_minutes: campaign.retryCooldownMinutes != null ? String(campaign.retryCooldownMinutes) : '',
    max_dial_attempts: campaign.maxDialAttempts != null ? String(campaign.maxDialAttempts) : '',
    // Pre-filled from the product's own current default when this campaign
    // has no override of its own yet, so the field shows the real number
    // being inherited rather than an empty box -- the admin can then edit
    // it, and only THAT edit becomes a real per-campaign override on save.
    // Saving unchanged still writes it explicitly, same as any other
    // pre-filled form field -- it stops tracking the product's value if
    // that's changed again later, which is the trade-off of showing a
    // concrete number instead of just a placeholder.
    daily_dial_cap: campaign.dailyDialCap != null
      ? String(campaign.dailyDialCap)
      : (productDailyDialCapDefault.value != null ? String(productDailyDialCapDefault.value) : ''),
    auto_adopt_matching_leads: campaign.autoAdoptMatchingLeads !== false,
    only_future_leads: !!campaign.onlyFutureLeads,
  }
  filters.value = hydrateCampaignFiltersFromApi(campaign.filters)
  // Skipped while a save's background reselection is still being polled --
  // the parent reloads the campaign as soon as the *first* 'saved' emit
  // fires (the "re-selecting in background" one), and this same hydrate()
  // runs again on that reload. Resetting unconditionally here would wipe
  // the in-flight poll's own state out from under it before it ever gets
  // to report back.
  if (assignmentPhase.value === 'idle') {
    stopAssignmentPoll()
    excelFile.value = null
    excelAssignments.value = []
    excelParseError.value = ''
    unmatchedAssignments.value = []
    assignmentMatchSummary.value = ''
  }
  // Unrelated to the Excel-assignment poll above -- a pending-but-unsaved
  // audio pick never survives a fresh campaign load either way, so this
  // always resets regardless of assignmentPhase.
  voiceAudioFile.value = null
  voiceAudioUploadError.value = ''
  // A stale preview from whatever campaign/settings were showing before
  // must not linger across a reload -- it would otherwise look like it
  // belongs to the newly-hydrated settings.
  voicePreviewUrl.value = ''
  voicePreviewError.value = ''
  // Mirrors CreateCampaignDialog's own hydrateFormFromCampaign: an existing
  // reminder campaign that already has an anchor_field set is counting from
  // a per-lead field, not the shared campaign clock -- reflect that here
  // too rather than defaulting back to CAMPAIGN on every reload.
  anchorMode.value = form.value.anchor_field ? 'LEAD_FIELD' : 'CAMPAIGN'
}
watch(
  () => props.campaign,
  (campaign) => {
    hydratingCampaign = true
    hydrate(campaign)
    nextTick(() => { hydratingCampaign = false })
  },
  { immediate: true },
)

// Derived from the FORM's own (possibly still-unsaved) handled_by, not the
// saved campaign prop -- so flipping the selector below immediately shows/
// hides the AI-only sections (Call script, Voice mode, Capture agreed time)
// without waiting for a save+reload round trip.
const isAiCampaign = computed(() => form.value.handled_by === 'AI_AGENT')
const isStaticVoice = computed(() => isAiCampaign.value && form.value.voice_mode === 'STATIC_VOICE')
const isRecurringReminder = computed(() => form.value.task_type === 'RECURRING_REMINDER')

// A brand-new campaign's Greeting starts pre-filled with the org's own
// default (see CreateCampaignDialog's defaultForm -- greeting:
// readGreetingMessageFromOrg(...)), so it's easy to launch real calls
// without ever noticing it still says the org's generic greeting instead
// of something written for THIS campaign. Confirmed live 2026-09-01:
// Prezence Solution's "Closed circle BNI members day" campaign ran its
// first 3 real calls introducing itself with the org's generic front-desk
// greeting instead of its own BNI-stall-followup one, because nothing
// flagged that the field still matched the default. This just surfaces
// that fact -- it never blocks saving or changes what gets sent.
const orgDefaultGreeting = computed(() => readGreetingMessageFromOrg(store.state.organization).trim())
const greetingMatchesOrgDefault = computed(() => {
  const current = String(form.value.greeting ?? '').trim()
  return Boolean(current) && Boolean(orgDefaultGreeting.value) && current === orgDefaultGreeting.value
})

// The org's one designated inbound campaign (org.default_incoming_task_id --
// named after the org itself, created by onboarding). EC2 answers a call
// landing on the org's number without ever reading this campaign's own
// greeting/mission_prompt -- it uses the org-level system_prompt set once in
// the BMS during onboarding instead (see provisionOrgDefaultCampaigns's own
// comment on this in index.mjs). So even if an owner later switches this one
// campaign to AI-handled, its Call script fields would be pure dead UI --
// edits here have no effect on what callers actually hear -- KAN-25.
const isDefaultInboundCampaign = computed(() => {
  const taskId = String(props.campaign?.campaignId ?? props.campaign?.id ?? '').trim()
  const defaultId = String(store.state.organization?.default_incoming_task_id ?? '').trim()
  return Boolean(taskId) && taskId === defaultId
})
const hideCallScript = computed(() => isStaticVoice.value || isDefaultInboundCampaign.value)

// The set Google's Gemini TTS confirms support for -- Urdu/Odia/Assamese
// are deliberately left off since they aren't on that list.
const VOICE_LANGUAGES = [
  { value: 'en-IN', label: 'English' },
  { value: 'hi-IN', label: 'Hindi' },
  { value: 'ta-IN', label: 'Tamil' },
  { value: 'te-IN', label: 'Telugu' },
  { value: 'kn-IN', label: 'Kannada' },
  { value: 'ml-IN', label: 'Malayalam' },
  { value: 'bn-IN', label: 'Bengali' },
  { value: 'mr-IN', label: 'Marathi' },
  { value: 'gu-IN', label: 'Gujarati' },
  { value: 'pa-IN', label: 'Punjabi' },
]

// A one-time AI campaign defaults auto_dial_enabled to false at creation
// (unlike a RECURRING_REMINDER one, always forced true -- see createTask) --
// turning it on here is the one place it can happen, and it means real
// calls start going out on the very next 5-minute scheduler tick, so it
// gets an explicit confirmation rather than a silent switch flip. Confirmed
// this was needed live 2026-08-18: a one-time campaign calling real people
// within minutes of creation, with no deliberate "go live" step, caught
// its own creator off guard.
const showAutoDialConfirm = ref(false)
function onAutoDialToggle(value) {
  if (value && isAiCampaign.value && !isRecurringReminder.value) {
    showAutoDialConfirm.value = true
    return // form.auto_dial_enabled stays false until confirmed
  }
  form.value.auto_dial_enabled = value
}
function confirmAutoDialToggle() {
  form.value.auto_dial_enabled = true
  showAutoDialConfirm.value = false
}
function cancelAutoDialToggle() {
  showAutoDialConfirm.value = false
}
const isOneTimeSchedule = computed(() => form.value.schedule_type === 'ONE_TIME')

// Falls back to the campaign-scoped `contexts` prop when the fuller
// `allContexts` prop isn't supplied by some caller -- keeps Lead filters
// working even without the Product picker's own full org list.
const contextsPool = computed(() => (props.allContexts && props.allContexts.length ? props.allContexts : props.contexts))

// The Product field is single-select in the UI, same "pick one, store as a
// one-item array" reconciliation as CreateCampaignDialog's own
// selectedContext -- form.context_names stays an array everywhere else
// (editTask's payload, filter-field resolution, the KB scoping pipeline).
const selectedContext = computed({
  get: () => (Array.isArray(form.value.context_names) ? form.value.context_names[0] : undefined) ?? '',
  set: (value) => { form.value.context_names = value ? [value] : [] },
})
const contextOptions = computed(() =>
  contextsPool.value
    .filter((context) => !context.disabled)
    .map((context) => ({ title: context.context_name, value: context.context_name })),
)

// Scoped to the FORM's own (possibly just-changed, unsaved) product
// selection rather than the campaign prop's already-saved one -- so
// switching products here immediately updates Lead filters/schedule-field
// pickers below instead of showing the previous product's fields until a
// save+reload round trip.
const selectedContexts = computed(() => {
  const names = new Set(
    (Array.isArray(form.value.context_names) ? form.value.context_names : [])
      .map((name) => String(name ?? '').trim().toLowerCase()),
  )
  return contextsPool.value.filter((context) =>
    names.has(String(context.context_name ?? context.contextName ?? '').trim().toLowerCase()),
  )
})
const canUseScheduleFilters = computed(() => form.value.context_names.length > 0)

const filterFieldOptions = computed(() => buildCampaignFilterFieldOptions(selectedContexts.value))

// Greeting/Mission get {agent_name}/{contact_name} plus every real field
// this campaign's product/context actually has, and every field this
// task's own "Capture caller details" is configured to collect --
// applyTokens (yandle_admin_portal_1/index.mjs) resolves ALL of these for
// both greeting and mission_prompt on every dial, identically to how it
// resolves them for the per-lead dialer script (voiceScriptVariables
// below), so there was never a real reason for this picker to offer fewer
// tokens than that one -- it just never got the same treatment. Reuses
// filterFieldOptions (same real-fields list Lead filters already resolve)
// rather than a separate illustrative/fake list, so what's offered here is
// guaranteed to actually exist for this campaign's product, and merges in
// capture_lead_fields (Settings -> Capture caller details) the same way
// CampaignSlotBooking.vue's confirmationMessageVariables already does for
// the slot-booking confirmation message.
const callScriptVariables = computed(() => {
  const base = [
    { token: 'agent_name', label: 'Agent name', example: 'Asha' },
    { token: 'contact_name', label: 'Contact name', example: 'Priya Sharma' },
  ]
  const seen = new Set(base.map((v) => v.token))
  const contextFields = filterFieldOptions.value
    .filter((f) => !seen.has(f.key))
    .map((f) => ({ token: f.key, label: f.label, example: f.placeholder || f.label }))
  contextFields.forEach((v) => seen.add(v.token))
  const captureFields = (Array.isArray(form.value.capture_lead_fields) ? form.value.capture_lead_fields : [])
    .filter((row) => row?.field && !seen.has(row.field))
    .map((row) => ({ token: row.field, label: row.description || row.field, example: `(${row.field})` }))
  return [...base, ...contextFields, ...captureFields]
})

// Voice Blast is per-lead too, and applyTokens resolves the exact same
// field set for it as for Greeting/Mission above -- kept as its own
// computed (rather than merged into callScriptVariables) only because the
// two pickers serve visually distinct fields and some future need might
// justify divergence again, but today they're intentionally identical.
const voiceScriptVariables = computed(() => callScriptVariables.value)
const dateFilterFieldOptions = computed(() =>
  filterFieldOptions.value.filter((field) => campaignFilterFieldKind(field) === 'date'),
)
// "Capture caller details" dropdown -- every real field this campaign's
// product has, any type (unlike the date/time-narrowed lists below), since
// an admin might want the AI to capture a TEXT/DROPDOWN/NUMBER/etc field
// just as often as a date.
const leadFieldOptions = computed(() => filterFieldOptions.value)
// "Also save the time to" -- narrowed to TIME-typed fields, same idea as
// CreateCampaignDialog's own timeFilterFieldOptions for schedule_time_field.
const timeFilterFieldOptions = computed(() =>
  filterFieldOptions.value.filter((field) => String(field.type ?? '').toUpperCase() === 'TIME'),
)
// Interval-field dropdown for a reminder's own "Every" cadence override --
// narrowed to number-typed fields (e.g. a per-customer contract term).
const numberFilterFieldOptions = computed(() =>
  filterFieldOptions.value.filter((field) => campaignFilterFieldKind(field) === 'number'),
)

const handledByOptions = CAMPAIGN_HANDLED_BY_OPTIONS
const agentOptions = computed(() => {
  const matched = props.agents
    .filter((user) => {
      const role = String(user.role ?? '').toUpperCase()
      if (role === 'AGENT') {
        return isAiCampaign.value
          ? isAiAgent(resolveListAgentType(user))
          : !isAiAgent(resolveListAgentType(user))
      }
      return !isAiCampaign.value && ['OWNER', 'ADMIN'].includes(role)
    })
    .map((user) => ({ title: user.name || user.email || user.id, value: user.id }))

  // A campaign's assigned_agent_ids can outlive the person it points at --
  // deleting a team member doesn't cascade-clean campaigns that had them
  // assigned (confirmed live on Uniqare's default inbound campaign: a
  // deleted user's id, present nowhere in yandle_organization_users
  // anymore, still sat in assigned_agent_ids). v-select has no item to
  // resolve a title from for an id like that, so it fell back to printing
  // the raw UUID as the chip -- KAN-29. Adding a synthetic entry for any
  // selected id not among the real matches gives the chip a legible label
  // instead, and since the field already has closable-chips, the admin can
  // see it's stale and remove it themselves with the existing X -- this
  // deliberately never auto-removes it, since that could leave a live
  // inbound campaign with zero assigned agents.
  const matchedIds = new Set(matched.map((opt) => opt.value))
  const orphaned = (form.value.assigned_agent_ids || [])
    .filter((id) => id && !matchedIds.has(id))
    .map((id) => ({ title: 'Removed team member', value: id }))

  return [...matched, ...orphaned]
})

// Who this campaign's AI can hand a call to -- NOT scoped to this
// campaign's own assigned_agent_ids (on an AI_AGENT campaign that list
// holds AI personas, never humans, so it can't double as the transfer
// roster). Any human at the org is eligible; each is offered only the
// delivery method(s) they actually have a contact point for.
const redirectTargetOptions = computed(() =>
  props.agents
    .filter((user) => {
      const role = String(user.role ?? '').toUpperCase()
      return ['OWNER', 'ADMIN'].includes(role) || (role === 'AGENT' && !isAiAgent(resolveListAgentType(user)))
    })
    .flatMap((user) => {
      const name = user.name || user.email || user.id
      const opts = []
      if (user.softphoneExtension) opts.push({ title: `${name} — softphone`, value: `agent:${user.id}` })
      if (user.mobileNumber) opts.push({ title: `${name} — mobile`, value: `phone:${user.id}` })
      return opts
    }),
)

const SCHEDULE_TYPE_OPTIONS = [
  { value: 'YEARLY', title: 'Yearly' },
  { value: 'MONTHLY', title: 'Monthly' },
  { value: 'WEEKLY', title: 'Weekly' },
  { value: 'DAILY', title: 'Daily' },
  { value: 'ONE_TIME', title: 'Once, at a time on the lead' },
]
const WEEKDAY_OPTIONS = [
  { value: 'MON', title: 'Monday' },
  { value: 'TUE', title: 'Tuesday' },
  { value: 'WED', title: 'Wednesday' },
  { value: 'THU', title: 'Thursday' },
  { value: 'FRI', title: 'Friday' },
  { value: 'SAT', title: 'Saturday' },
  { value: 'SUN', title: 'Sunday' },
]
const DAY_OF_MONTH_OPTIONS = [
  ...Array.from({ length: 31 }, (_, i) => ({ value: String(i + 1), title: String(i + 1) })),
  { value: 'LAST', title: 'Last day of the month' },
]
const scheduleTypeHint = computed(() => {
  switch (form.value.schedule_type) {
    case 'YEARLY':
      return 'Repeats every N years on a chosen month/day -- either every lead’s own reference date (e.g. a birthday) or one fixed date shared by everyone.'
    case 'MONTHLY':
      return 'Repeats every N months on a chosen day of the month.'
    case 'WEEKLY':
      return 'Repeats every N weeks on a chosen day of the week.'
    case 'DAILY':
      return 'Repeats every N days.'
    case 'ONE_TIME':
      return 'Calls each lead once, at the exact date and time held in the field below, then never again.'
    default:
      return ''
  }
})
const scheduleFrequencyUnitLabel = computed(() => {
  switch (form.value.schedule_type) {
    case 'YEARLY': return 'year(s)'
    case 'MONTHLY': return 'month(s)'
    case 'WEEKLY': return 'week(s)'
    default: return 'day(s)'
  }
})

// 'CAMPAIGN' = every lead anchors to the same shared date; 'LEAD_FIELD' =
// each lead counts from its own value in form.anchor_field instead. See
// hydrate()'s own comment above for how this is set on load.
const anchorMode = ref('CAMPAIGN')
watch(anchorMode, (mode) => {
  if (mode === 'CAMPAIGN') form.value.anchor_field = ''
  else if (mode === 'LEAD_FIELD' && form.value.schedule_type !== 'ONE_TIME') form.value.anchor_date = ''
})
watch(
  () => form.value.schedule_type,
  (type) => {
    if (type === 'ONE_TIME') anchorMode.value = 'LEAD_FIELD'
  },
)

// Switching handled_by invalidates the agent selection (different pool) --
// guarded off during hydration, same reasoning as CreateCampaignDialog's
// own matching watcher.
watch(
  () => form.value.handled_by,
  (next, prev) => {
    if (hydratingCampaign) return
    if (!prev || next === prev) return
    form.value.assigned_agent_ids = []
    if (next === 'AI_AGENT' && !AI_CAMPAIGN_DISTRIBUTION_MODES.has(form.value.distribution_mode)) {
      form.value.distribution_mode = 'ROUND_ROBIN'
    }
  },
)

// Distribution mode -- AI campaigns are narrowed to the modes that actually
// assign a specific agent per lead, matching the backend's own
// createTask/editTask validation (see the matching comment there).
const AI_CAMPAIGN_DISTRIBUTION_MODES = new Set(['ROUND_ROBIN', 'EXCEL', 'MANUAL'])
const distributionModeOptions = computed(() => {
  let modes = isAiCampaign.value
    ? CAMPAIGN_DISTRIBUTION_MODES.filter((mode) => AI_CAMPAIGN_DISTRIBUTION_MODES.has(mode.value))
    : CAMPAIGN_DISTRIBUTION_MODES
  // An AI campaign saved before this restriction existed (e.g. on OPEN)
  // still needs its own current value shown here rather than looking
  // blank/broken -- the backend's own validation forces a real change the
  // next time this is actually saved.
  const currentMode = String(props.campaign?.distributionMode ?? '').toUpperCase()
  if (isAiCampaign.value && currentMode && !modes.some((mode) => mode.value === currentMode)) {
    const original = CAMPAIGN_DISTRIBUTION_MODES.find((mode) => mode.value === currentMode)
    if (original) modes = [original, ...modes]
  }
  // Excel's whole point is assigning an uploaded batch of EXISTING leads --
  // not offered while "only future leads" is on, since combining them
  // would be a silent no-op on the backend.
  if (form.value.only_future_leads) {
    modes = modes.filter((mode) => mode.value !== 'EXCEL')
  }
  return modes
})
const isExcelMode = computed(() => form.value.distribution_mode === 'EXCEL')
const isFieldBasedMode = computed(() => form.value.distribution_mode === 'FIELD_BASED')
const agentFieldSuggestions = computed(() => buildAgentFieldSuggestions(filterFieldOptions.value))

// Static Voice: uploaded audio can't have a lead's name spliced in, so
// switching to it forces personalization back to the one setting it
// actually supports -- matches editTask's own server-side coercion.
watch(() => form.value.voice_source, (source) => {
  if (source === 'UPLOADED_AUDIO') form.value.voice_personalization = 'SAME_FOR_ALL'
})

const voiceAudioFile = ref(null)
const voiceAudioUploading = ref(false)
const voiceAudioUploadError = ref('')

/**
 * Uploads the raw .wav immediately on selection (not deferred to Save) --
 * editTask's own validation requires voice_audio_raw_key to already exist
 * before a STATIC_VOICE/UPLOADED_AUDIO save is accepted, same precondition
 * getTaskVoiceAudioUploadUrl documents server-side.
 */
async function onVoiceAudioFileChange(fileOrFiles) {
  voiceAudioUploadError.value = ''
  const file = Array.isArray(fileOrFiles) ? fileOrFiles[0] : fileOrFiles
  if (!file) return
  const taskId = String(props.campaign?.campaignId ?? props.campaign?.id ?? '').trim()
  if (!taskId) return
  voiceAudioUploading.value = true
  try {
    const urlResponse = await fetchTaskVoiceAudioUploadUrl({ task_id: taskId })
    if (!isApiSuccess(urlResponse)) {
      voiceAudioUploadError.value = getApiMessage(urlResponse, 'Failed to get an upload URL')
      return
    }
    const result = unwrapApiPayload(urlResponse) ?? urlResponse?.data ?? urlResponse ?? {}
    const uploadUrl = String(result.upload_url ?? result.uploadUrl ?? '').trim()
    const s3Key = String(result.s3_key ?? result.s3Key ?? '').trim()
    if (!uploadUrl || !s3Key) {
      voiceAudioUploadError.value = 'Upload URL missing from server response.'
      return
    }
    const putResult = await putCampaignVoiceAudio(uploadUrl, file)
    if (!putResult.ok) {
      voiceAudioUploadError.value = putResult.error || 'Audio upload failed'
      return
    }
    form.value.voice_audio_raw_key = s3Key
    form.value.voice_audio_filename = file.name
  } finally {
    voiceAudioUploading.value = false
  }
}

const voicePreviewUrl = ref('')
const voicePreviewLoading = ref(false)
const voicePreviewError = ref('')

// Reads back whatever was last SAVED on the campaign record, same as the
// backend endpoint itself does -- not the unsaved form state above, so this
// stays keyed off props.campaign rather than form.value.voice_mode.
const canPreviewVoice = computed(() => {
  if (!isStaticVoice.value) return false
  return String(props.campaign?.voiceMode ?? '').toUpperCase() === 'STATIC_VOICE'
})

async function previewVoice() {
  const taskId = String(props.campaign?.campaignId ?? props.campaign?.id ?? '').trim()
  if (!taskId) return
  voicePreviewError.value = ''
  voicePreviewUrl.value = ''
  voicePreviewLoading.value = true
  try {
    const response = await fetchTaskVoiceAudioPreviewUrl({ task_id: taskId })
    if (!isApiSuccess(response)) {
      voicePreviewError.value = getApiMessage(response, 'Failed to generate preview')
      return
    }
    const result = unwrapApiPayload(response) ?? response?.data ?? response ?? {}
    const url = String(result.preview_data_url ?? result.previewDataUrl ?? '').trim()
    if (!url) {
      voicePreviewError.value = 'Preview URL missing from server response.'
      return
    }
    voicePreviewUrl.value = url
  } finally {
    voicePreviewLoading.value = false
  }
}

const excelFile = ref(null)
const excelAssignments = ref([])
const excelParseError = ref('')
const parsingExcel = ref(false)
const unmatchedAssignments = ref([])
/** idle | uploading | processing */
const assignmentPhase = ref('idle')
const assignmentMatchSummary = ref('')
let assignmentPollTimer = null
const ASSIGNMENT_POLL_MS = 3000
const ASSIGNMENT_POLL_MAX_MS = 15 * 60 * 1000

function stopAssignmentPoll() {
  if (assignmentPollTimer) {
    clearTimeout(assignmentPollTimer)
    assignmentPollTimer = null
  }
}
function sleep(ms) {
  return new Promise((resolve) => { assignmentPollTimer = setTimeout(resolve, ms) })
}
async function pollAssignmentStatus(uploadId) {
  const started = Date.now()
  while (Date.now() - started < ASSIGNMENT_POLL_MAX_MS) {
    await sleep(ASSIGNMENT_POLL_MS)
    assignmentPollTimer = null
    const response = await fetchCampaignAssignmentUploadStatus({ upload_id: uploadId })
    if (!isApiSuccess(response)) continue
    const statusPayload = normalizeAssignmentUploadStatus(response)
    if (statusPayload.status === 'COMPLETED' || statusPayload.status === 'FAILED') return statusPayload
  }
  return { uploadId, status: 'FAILED', matchedCount: null, unmatchedCount: null, unmatched: [], timedOut: true }
}
function applyAssignmentStatusResult(statusPayload) {
  if (statusPayload.timedOut) {
    emit('error', 'Assignment is still processing. Refresh in a moment -- it will finish in the background.')
    assignmentPhase.value = 'idle'
    return
  }
  if (statusPayload.status === 'FAILED') {
    emit(
      'error',
      isFieldBasedMode.value
        ? 'Lead assignment failed. Check the agent field and assigned agents, then try again.'
        : 'Lead assignment failed. Check the spreadsheet and try again.',
    )
    assignmentPhase.value = 'idle'
    return
  }
  const matched = statusPayload.matchedCount
  const unmatchedCount = statusPayload.unmatchedCount
  const alreadyPresent = statusPayload.alreadyPresentCount
  const parts = []
  if (matched != null) parts.push(`${matched} matched`)
  if (alreadyPresent != null && alreadyPresent > 0) parts.push(`${alreadyPresent} already in campaign`)
  if (unmatchedCount != null) parts.push(`${unmatchedCount} unmatched`)
  assignmentMatchSummary.value = parts.join(', ')
  const unmatched =
    statusPayload.unmatched.length > 0 ? statusPayload.unmatched : normalizeUnmatchedAssignments(statusPayload)
  assignmentPhase.value = 'idle'
  unmatchedAssignments.value = unmatched
  emit('saved', unmatched.length ? 'Lead assignments finished -- some leads were unmatched.' : 'Lead assignments finished.')
}
async function onExcelFileChange(fileOrFiles) {
  excelAssignments.value = []
  excelParseError.value = ''
  unmatchedAssignments.value = []
  const file = Array.isArray(fileOrFiles) ? fileOrFiles[0] : fileOrFiles
  if (!file) return
  parsingExcel.value = true
  try {
    const result = await parseExcelAssignmentsFile(file)
    if (!result.ok) {
      excelParseError.value = result.error
      return
    }
    excelAssignments.value = result.assignments
  } finally {
    parsingExcel.value = false
  }
}

function addFilterRow() {
  filters.value = [...filters.value, createEmptyCampaignFilter()]
}
function removeFilterRow(index) {
  filters.value = filters.value.filter((_, i) => i !== index)
}
function onFilterFieldChange(filter) {
  filter.values = []
  if (filter.field && typeof filter.field === 'object') {
    filter.field = String(filter.field.key ?? filter.field.value ?? '').trim()
  }
  const ops = operatorsForRow(filter)
  if (!ops.some((op) => op.value === filter.operator)) {
    filter.operator = ops[0]?.value || 'EQUALS'
  }
}
function onFilterOperatorChange(filter) {
  filter.values = []
}
function filterRowDef(filter) {
  return findCampaignFilterFieldDef(filterFieldOptions.value, filter.field)
}
function filterRowKind(filter) {
  return campaignFilterFieldKind(filterRowDef(filter))
}
function operatorsForRow(filter) {
  return operatorsForFilterField(filterRowDef(filter))
}
function isRangeOperator(operator) {
  return String(operator ?? '').toUpperCase() === 'BETWEEN'
}
function isDateCompareOperator(operator) {
  const op = String(operator ?? '').toUpperCase()
  return op === 'AFTER' || op === 'BEFORE'
}
function isNumericCompareOperator(operator) {
  const op = String(operator ?? '').toUpperCase()
  return op === 'GREATER_THAN' || op === 'LESS_THAN'
}
function filterValueOptions(filter) {
  const def = filterRowDef(filter)
  return def?.type === 'DROPDOWN' && Array.isArray(def.options) ? def.options : []
}
function filterValuesPlaceholder(filter) {
  const def = filterRowDef(filter)
  return def?.type === 'DROPDOWN' ? 'Select value' : 'Value'
}

async function submit() {
  const taskId = String(props.campaign?.campaignId ?? props.campaign?.id ?? '').trim()
  if (!taskId) return

  if (!String(form.value.task_name ?? '').trim()) {
    emit('error', 'Campaign name is required.')
    return
  }
  if (!form.value.context_names.length) {
    emit('error', 'Select a product.')
    return
  }
  if (!form.value.assigned_agent_ids.length) {
    emit('error', `Assign at least one ${isAiCampaign.value ? 'AI' : 'human'} agent.`)
    return
  }
  if (!isRecurringReminder.value) {
    if (!form.value.start_date || !form.value.end_date) {
      emit('error', 'Start date and end date are required.')
      return
    }
    if (form.value.end_date < form.value.start_date) {
      emit('error', 'End date must be after the start date.')
      return
    }
  } else {
    // Same belt-and-braces reasoning as CreateCampaignDialog's own submit()
    // -- a native <input type="date"> inside FieldWithLeadOverride's FIXED
    // mode doesn't reliably participate in any framework-level validation,
    // so this is checked explicitly rather than trusted to a form rule.
    if (isOneTimeSchedule.value && !String(form.value.anchor_date ?? '').trim() && !String(form.value.anchor_field ?? '').trim()) {
      emit('error', 'Date to call is required -- pick a fixed date or a lead field above.')
      return
    }
    if (form.value.schedule_type === 'YEARLY' && anchorMode.value === 'CAMPAIGN' && !String(form.value.anchor_date ?? '').trim()) {
      emit('error', 'Date is required -- pick a date, or switch to "A lead field" above.')
      return
    }
  }

  if (isExcelMode.value && !excelAssignments.value.length) {
    emit('error', 'Upload an Excel/CSV assignment file first.')
    return
  }

  if (isStaticVoice.value) {
    if (voiceAudioUploading.value) {
      emit('error', 'Still uploading the audio file -- wait for it to finish.')
      return
    }
    if (form.value.voice_source === 'UPLOADED_AUDIO' && !form.value.voice_audio_raw_key) {
      emit('error', 'Upload a .wav file first.')
      return
    }
    if (form.value.voice_source === 'POLLY_TEXT' && !String(form.value.voice_script_text ?? '').trim()) {
      emit('error', 'Type a message first.')
      return
    }
  }

  saving.value = true
  try {
    const payload = {
      task_id: taskId,
      task_name: String(form.value.task_name ?? '').trim(),
      description: String(form.value.description ?? '').trim(),
      context_names: form.value.context_names,
      handled_by: form.value.handled_by,
      assigned_agent_ids: form.value.assigned_agent_ids,
      filters: normalizeCampaignFiltersForApi(filters.value, filterFieldOptions.value),
      distribution_mode: form.value.distribution_mode,
      agent_field: isFieldBasedMode.value ? String(form.value.agent_field ?? '').trim() : '',
      auto_adopt_matching_leads: Boolean(form.value.auto_adopt_matching_leads),
      only_future_leads: Boolean(form.value.only_future_leads),
    }
    if (isRecurringReminder.value) {
      payload.anchor_field = String(form.value.anchor_field ?? '').trim()
      payload.anchor_date = String(form.value.anchor_date ?? '').trim()
      payload.schedule_type = form.value.schedule_type
      payload.schedule_type_field = form.value.schedule_type_field
      payload.schedule_frequency = form.value.schedule_frequency
      payload.schedule_frequency_field = form.value.schedule_frequency_field
      payload.schedule_time = form.value.schedule_time
      payload.schedule_time_field = form.value.schedule_time_field
      payload.schedule_day_of_week = form.value.schedule_day_of_week
      payload.schedule_day_of_week_field = form.value.schedule_day_of_week_field
      payload.schedule_day_of_month = form.value.schedule_day_of_month
      payload.schedule_day_of_month_field = form.value.schedule_day_of_month_field
    } else {
      payload.start_date = form.value.start_date
      payload.end_date = form.value.end_date
    }
    // Transfer targets are sent for every campaign now, not just AI/default
    // ones -- the transfer router itself is org-wide (see the template's own
    // comment on this section), so a Human-agent campaign's own transfer
    // target is just as reachable from an inbound call as an AI campaign's.
    // Kept as its own unconditional block, separate from the isAiCampaign-
    // only block below, since it needs sending without needing
    // greeting/mission_prompt/voice_mode at all.
    {
      // Always sent, including empty: clearing the list is a real choice
      // ("stop offering a transfer on this campaign"), and an omitted
      // field would be read as "leave unchanged".
      payload.redirect_targets = Array.isArray(form.value.redirect_targets) ? form.value.redirect_targets : []
      // Blank clears back to the platform default line.
      payload.redirect_message = String(form.value.redirect_message ?? '').trim()
      // Blank falls back to the campaign's own name in the router prompt.
      payload.redirect_description = String(form.value.redirect_description ?? '').trim()
    }
    if (isAiCampaign.value) {
      payload.greeting = String(form.value.greeting ?? '').trim()
      payload.mission_prompt = String(form.value.mission_prompt ?? '').trim()
      payload.disclaimer_enabled = form.value.disclaimer_enabled
      payload.voice_mode = form.value.voice_mode
      // AI-to-AI transfer (KAN-44): only meaningful for an AI-Speak campaign
      // -- the backend rejects true for Static Voice or Human-agent (no live
      // AI session to hand a call to), so forced false here rather than
      // risking the whole save erroring out over a toggle the UI hid anyway.
      payload.ai_transfer_eligible = form.value.voice_mode === 'AI_SPEAK'
        ? Boolean(form.value.ai_transfer_eligible)
        : false
      if (isStaticVoice.value) {
        payload.voice_source = form.value.voice_source
        payload.voice_personalization = form.value.voice_personalization
        payload.voice_script_text = form.value.voice_source === 'POLLY_TEXT'
          ? String(form.value.voice_script_text ?? '').trim()
          : ''
        payload.voice_audio_raw_key = form.value.voice_source === 'UPLOADED_AUDIO'
          ? String(form.value.voice_audio_raw_key ?? '').trim()
          : ''
        payload.voice_audio_filename = form.value.voice_source === 'UPLOADED_AUDIO'
          ? String(form.value.voice_audio_filename ?? '').trim()
          : ''
        // Only meaningful for typed text -- an uploaded file has no
        // language to pick, so this rides along harmlessly unused rather
        // than being omitted, same pattern as the other voice_* fields
        // above.
        payload.voice_language = String(form.value.voice_language ?? '').trim() || 'en-IN'
      }
      // capture_datetime_field/_time_field/_prompt are deliberately NOT sent
      // from here -- Slot booking (its own tab, CampaignSlotBooking.vue) owns
      // saving those now, so this save can never clobber them with a stale
      // form value. editTask's own undefined-means-leave-as-is pattern
      // (confirmed in its own comment) makes omitting them here safe.
      // Blank rows (an "Add field" click the admin never finished) are
      // dropped rather than sent -- editTask's own validateCaptureLeadFields
      // would reject them outright (both field and prompt are mandatory per
      // row), so filtering here avoids a save failing over an incomplete
      // row the admin may not have even meant to keep.
      payload.capture_lead_fields = (form.value.capture_lead_fields || [])
        .map((row) => ({
          field: String(row?.field ?? '').trim(),
          prompt: String(row?.prompt ?? '').trim(),
          // Optional -- admin-facing label only (variable-picker display),
          // never read by the AI. Falls back to the field name itself
          // wherever it's blank, so an older row with no description
          // still shows something sensible.
          description: String(row?.description ?? '').trim(),
        }))
        .filter((row) => row.field && row.prompt)
    }
    payload.auto_dial_enabled = Boolean(form.value.auto_dial_enabled)
    payload.restrict_to_known_callers = Boolean(form.value.restrict_to_known_callers)
    if (form.value.auto_dial_enabled) {
      payload.working_hours_start = String(form.value.working_hours_start ?? '').trim()
      payload.working_hours_end = String(form.value.working_hours_end ?? '').trim()
      // HUMAN softphone predictive: dial_ratio Int (schema). Locked at 1
      // until phone-system parallel connect queuing is ready.
      if (!isAiCampaign.value) payload.dial_ratio = 1
    }
    // retry_cooldown_minutes, max_dial_attempts and daily_dial_cap are NOT
    // auto_dial_enabled-gated -- all three protect the person being called
    // rather than throttle the scheduler, so all apply to a manually-run
    // campaign too (a manual "Run AI calls" click redials the same lead just
    // as much as auto-dial does). Sent even when blank so clearing any of
    // them back to "use the platform/product default" is a real choice, not
    // just omitted. retry_cooldown_minutes used to be sent only when
    // auto_dial_enabled, which meant a manual campaign's own value was
    // discarded on save and it was silently pinned at the unconfigurable
    // 15-minute platform floor -- same gap max_dial_attempts was already
    // fixed for.
    payload.retry_cooldown_minutes = form.value.retry_cooldown_minutes ? Number(form.value.retry_cooldown_minutes) : null
    payload.max_dial_attempts = form.value.max_dial_attempts ? Number(form.value.max_dial_attempts) : null
    payload.daily_dial_cap = form.value.daily_dial_cap ? Number(form.value.daily_dial_cap) : null

    const response = await editCampaign(payload)
    if (!isApiSuccess(response)) {
      emit('error', getApiMessage(response, 'Failed to save campaign settings'))
      return
    }

    // Changing distribution_mode/filters/agent_field can trigger the
    // backend to re-run lead selection in the background (merge-only --
    // existing leads are never touched or removed), same as create does.
    const result = unwrapApiPayload(response) ?? response?.data ?? response ?? {}
    const uploadUrl = String(result.upload_url ?? result.uploadUrl ?? '').trim()
    const uploadId = String(result.upload_id ?? result.uploadId ?? '').trim()
    const responseStatus = String(result.task_status ?? result.status ?? result.taskStatus ?? '').trim().toUpperCase()
    const needsAsync = Boolean(uploadUrl || uploadId) || responseStatus === 'PROCESSING'

    if (!needsAsync) {
      unmatchedAssignments.value = normalizeUnmatchedAssignments(response)
      emit('saved', getApiMessage(response, 'Campaign settings saved.'))
      return
    }

    emit('saved', getApiMessage(response, 'Settings saved. Re-selecting leads in the background…'))

    if (uploadUrl) {
      assignmentPhase.value = 'uploading'
      const putResult = await putCampaignAssignmentCsv(uploadUrl, excelAssignments.value)
      if (!putResult.ok) {
        emit('error', putResult.error || 'Failed to upload assignment file')
        assignmentPhase.value = 'idle'
        return
      }
    }

    if (!uploadId) {
      emit('error', uploadUrl
        ? 'Assignment uploaded, but upload_id was missing — refresh this page to check status.'
        : 'Saved, but upload_id was missing — refresh this page to check status.')
      assignmentPhase.value = 'idle'
      return
    }

    assignmentPhase.value = 'processing'
    const statusPayload = await pollAssignmentStatus(uploadId)
    applyAssignmentStatusResult(statusPayload)
  } finally {
    saving.value = false
  }
}
</script>

<style scoped>
.tw-adv-settings {
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.tw-adv-filters {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 8px;
}
.tw-capture-fields {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.tw-capture-fields__row {
  display: grid;
  grid-template-columns: 1fr 1.3fr 1.3fr auto;
  gap: 8px;
  align-items: center;
}
.tw-capture-fields__group {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 10px;
  border: 1px solid var(--y-border-subtle, #e5e7eb);
  border-radius: 8px;
}
.tw-capture-fields__agreed-time {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: var(--y-text-secondary, #6b7280);
  cursor: pointer;
}
.tw-capture-fields__split {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-left: 22px;
  padding: 10px;
  background: var(--y-surface-subtle, #f9fafb);
  border-radius: 6px;
}
.tw-adv-filter-row {
  display: grid;
  grid-template-columns: 1.1fr 0.9fr 1.1fr auto;
  gap: 8px;
  align-items: center;
}
.tw-adv-filter-row--range {
  grid-template-columns: 1.1fr 0.9fr 1.1fr auto;
}
.tw-adv-filter-range {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}
.tw-adv-settings__footer {
  display: flex;
  justify-content: flex-end;
  padding-top: 4px;
}
.tw-field-hint {
  margin: 4px 0 0;
  font-size: 0.8rem;
  line-height: 1.5;
  color: var(--y-text-muted, #64748b);
}
.tw-field-warning {
  margin: 6px 0 0;
  padding: 6px 10px;
  border-radius: 6px;
  font-size: 0.8rem;
  line-height: 1.5;
  color: var(--y-warning-text, #92400e);
  background: var(--y-warning-bg, rgba(217, 119, 6, 0.12));
  border: 1px solid var(--y-warning-border, rgba(217, 119, 6, 0.3));
}
.tw-dialing-summary {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 12px;
  margin-bottom: 4px;
  border-radius: 10px;
  background: rgba(var(--v-theme-primary), 0.08);
}
.tw-dialing-summary__icon {
  margin-top: 2px;
  flex-shrink: 0;
  color: rgb(var(--v-theme-primary));
}
.tw-dialing-summary__text {
  margin: 0;
  font-size: 0.8125rem;
  line-height: 1.5;
  color: var(--y-text, inherit);
}
.tw-adv-note {
  margin: 6px 0 0;
  font-size: 0.8rem;
  font-weight: 600;
}
.tw-adv-note--ok {
  color: #047857;
}
.tw-adv-note--error {
  color: #b91c1c;
}
.tw-voice-preview {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  padding-top: 4px;
  border-top: 1px dashed rgba(148, 163, 184, 0.3);
}
.tw-voice-preview__player {
  width: 100%;
  max-width: 360px;
  height: 36px;
}
.tw-adv-processing {
  display: flex;
  align-items: center;
  gap: 0.7rem;
  padding: 0.75rem 0.9rem;
  border-radius: 12px;
  background: rgba(30, 77, 255, 0.06);
  border: 1px solid rgba(30, 77, 255, 0.14);
  font-size: 0.85rem;
}
.v-theme--dark .tw-adv-processing {
  background: rgba(129, 140, 248, 0.12);
  border-color: rgba(129, 140, 248, 0.22);
}
.tw-adv-unmatched {
  margin-top: 8px;
  border: 1px solid rgba(148, 163, 184, 0.2);
  border-radius: 10px;
  overflow: hidden;
  font-size: 0.8rem;
}
.tw-adv-unmatched__row {
  display: grid;
  grid-template-columns: 1fr 1fr 1.3fr;
  gap: 8px;
  padding: 7px 10px;
  border-top: 1px solid rgba(148, 163, 184, 0.16);
}
.tw-adv-unmatched__row:first-child {
  border-top: none;
}
.tw-adv-unmatched__row--head {
  font-weight: 700;
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  opacity: 0.65;
  background: rgba(148, 163, 184, 0.08);
}
@media (max-width: 780px) {
  .tw-adv-filter-row {
    grid-template-columns: 1fr;
  }
  .tw-adv-unmatched__row {
    grid-template-columns: 1fr;
  }
}
</style>
